import { useCallback, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { cn } from "@/lib/utils"
import { MetricLabel } from "../MetricLabel/MetricLabel"

export type LongShortMetricFormat = "pct" | "money" | "ratio" | "number"

export interface LongShortMetric {
  label: string
  long: number
  short: number
  format: LongShortMetricFormat
  definition?: string
}

export interface LongShortSummary {
  longs: number
  shorts: number
  longsWonPct: number
  shortsWonPct: number
}

export interface EntriesByTimePoint {
  label: string
  asia?: number
  europe?: number
  usa?: number
}

export interface EntriesByTimeGranularity {
  id: string
  label: string
  data: EntriesByTimePoint[]
}

export interface LongShortSplitProps {
  summary: LongShortSummary
  metrics: LongShortMetric[]
  granularities?: EntriesByTimeGranularity[]
  defaultGranularityId?: string
  className?: string
}

const TOOLTIP_STYLE = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  fontSize: 11,
  color: "var(--foreground)",
  padding: "6px 8px",
} as const

const LONG_COLOR = "#26a69a"
const SHORT_COLOR = "#f23645"
const SESSION_COLORS: Record<"asia" | "europe" | "usa", string> = {
  asia: "#f59e0b",
  europe: "#26a69a",
  usa: "var(--muted-foreground)",
}

function formatMetricValue(m: LongShortMetric, val: number): string {
  switch (m.format) {
    case "pct":
      return `${val.toFixed(0)}%`
    case "money":
      return `$${Math.abs(val).toFixed(0)}`
    case "ratio":
      return val.toFixed(2)
    default:
      return `${val}`
  }
}

export function LongShortSplit({
  summary,
  metrics,
  granularities,
  defaultGranularityId,
  className,
}: LongShortSplitProps) {
  const [granularityId, setGranularityId] = useState<string>(
    defaultGranularityId ?? granularities?.[0]?.id ?? "",
  )
  const [sessions, setSessions] = useState({ asia: true, europe: true, usa: true })

  const toggleSession = useCallback((key: "asia" | "europe" | "usa") => {
    setSessions((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const currentGranularity = granularities?.find((g) => g.id === granularityId)

  const chartData = metrics.map((m) => ({
    label: m.label,
    long: m.long,
    short: m.short,
  }))

  return (
    <div className={className}>
      <div className="grid grid-cols-2 gap-4 px-4 py-3 border-b border-border">
        <div className="flex flex-col">
          <MetricLabel label="Long Trades" className="text-caption text-muted-foreground" />
          <span className="text-section text-foreground">{summary.longs}</span>
          <span className="text-caption text-muted-foreground">
            {summary.longsWonPct.toFixed(0)}% won
          </span>
        </div>
        <div className="flex flex-col">
          <MetricLabel label="Short Trades" className="text-caption text-muted-foreground" />
          <span className="text-section text-foreground">{summary.shorts}</span>
          <span className="text-caption text-muted-foreground">
            {summary.shortsWonPct.toFixed(0)}% won
          </span>
        </div>
      </div>

      <div className="border-b border-border px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-section text-foreground">Performance Comparison</div>
          <div className="flex items-center gap-3 text-caption text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: LONG_COLOR }}
              />
              Long
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: SHORT_COLOR }}
              />
              Short
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              width={75}
              interval={0}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
              cursor={{ fill: "transparent" }}
              formatter={(value) => {
                const n = Number(value)
                if (!Number.isFinite(n)) return String(value)
                return Number.isInteger(n) ? n : n.toFixed(2)
              }}
            />
            <Bar dataKey="long" name="Long" fill={LONG_COLOR} barSize={8} radius={[0, 2, 2, 0]} />
            <Bar dataKey="short" name="Short" fill={SHORT_COLOR} barSize={8} radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="border-b border-border px-4 py-4">
        <table className="w-full text-caption">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-caption text-muted-foreground py-1.5 uppercase tracking-wider">
                Metric
              </th>
              <th className="text-right py-1.5" style={{ color: LONG_COLOR }}>
                Long
              </th>
              <th className="text-right py-1.5" style={{ color: SHORT_COLOR }}>
                Short
              </th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.label} className="border-b border-border last:border-b-0">
                <td className="text-caption text-muted-foreground py-1.5">
                  <MetricLabel label={m.label} definition={m.definition} />
                </td>
                <td className="text-right text-body text-foreground py-1.5">
                  {formatMetricValue(m, m.long)}
                </td>
                <td className="text-right text-body text-foreground py-1.5">
                  {formatMetricValue(m, m.short)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {currentGranularity && granularities && (
        <div className="border-b border-border px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-section text-foreground">Entries by Time</div>
            <div className="flex items-center gap-1">
              {granularities.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGranularityId(g.id)}
                  className={cn(
                    "text-caption px-2 py-0.5 rounded cursor-pointer transition-colors",
                    granularityId === g.id
                      ? "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-2">
            {(Object.keys(SESSION_COLORS) as Array<"asia" | "europe" | "usa">).map(
              (key) => (
                <label
                  key={key}
                  className="flex items-center gap-1.5 cursor-pointer text-caption text-muted-foreground"
                >
                  <input
                    type="checkbox"
                    checked={sessions[key]}
                    onChange={() => toggleSession(key)}
                    className="w-3 h-3 rounded"
                    style={{ accentColor: SESSION_COLORS[key] }}
                  />
                  <span className="capitalize">{key}</span>
                </label>
              ),
            )}
          </div>

          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={currentGranularity.data}
              margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                interval={currentGranularity.id === "hourly" ? 2 : 0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                width={30}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
                cursor={{ fill: "transparent" }}
              />
              {sessions.asia && (
                <Bar
                  dataKey="asia"
                  name="Asia"
                  fill={SESSION_COLORS.asia}
                  stackId="entries"
                />
              )}
              {sessions.europe && (
                <Bar
                  dataKey="europe"
                  name="Europe"
                  fill={SESSION_COLORS.europe}
                  stackId="entries"
                />
              )}
              {sessions.usa && (
                <Bar
                  dataKey="usa"
                  name="USA"
                  fill={SESSION_COLORS.usa}
                  stackId="entries"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
